import * as React from "react";
import FormContext, { initFormStore, updateFormValues } from "@/contexts/Form";
import Article from "@/components/molecules/Article";
import MiniTitle from "@/components/atoms/MiniTitle";
import FormBlock from "@/components/molecules/FormBlock";
import PropertyType from "@/constants/PropertyType";
import FormContainer from "@/components/atoms/FormContainer";
import { balconyDirectionOptions } from "@/constants/BalconyDirection";
import { floorPlanOptions } from "@/constants/FloorPlan";
import { structureOptions } from "@/constants/Structure";
import { connectingDirectionOptions, IConnectingDirection } from "@/constants/ConnectingDirection";
import { connectingTypeOptions, IConnectingType } from "@/constants/ConnectingType";
import { range } from "@/utils/number";
import form, { defaultIfBlank, ConnectingDetail, Connecting, ifResponseValueBlank } from "@/utils/form";
import {
  switchConnectingRoadStatusNumber,
  IConnectingStatus,
  connectingStatusOptions,
} from "@/constants/ConnectingStatus";
import NextButton from "@/components/molecules/NextButton";
import { validatePropertyDetail as validator, validateConnecting as connectingValidator } from "@/utils/validation";
import { useFormHandler } from "@/utils/hooks";
import { isBlank } from "@/utils/is";
// FIXME: eslint fix かけても問題なくなったら object-curly-newline を修正する
// eslint-disable-next-line object-curly-newline
import contract, { Feature, HIDDEN_COMPONENT_OPTIONS, isHiddenComponent, ColorThemeExtras } from "@/utils/contract";
import ContractContext, { initContractStore } from "@/contexts/Contract";

type PropertyDetailBlockProps = {
  onNext: () => void;
};

const PropertyDetailBlockOld = ({ onNext }: PropertyDetailBlockProps) => {
  const {
    state: { values, validates, units },
    dispatch,
    isValidProperty,
    isValidPropertyDetails,
    isRequiredPropertyDetails,
  } = React.useContext(FormContext) || initFormStore(form);

  const {
    state: { featuresObj },
  } = React.useContext(ContractContext) || initContractStore(contract);

  const firstRef = React.useRef(true);
  const [unit, setUnit] = React.useState("");
  const handleRoomNumber = useFormHandler("roomNumber", validator.roomNumber);
  const handleStory = useFormHandler("story", validator.story);
  const handleUnitSize = useFormHandler("unitSize", validator.unitSize);
  const handleBalconyDirection = useFormHandler("balconyDirection", validator.balconyDirection);
  const handleFloorPlan = useFormHandler("floorPlan", validator.floorPlan);
  const handleLandSize = useFormHandler("landSize", validator.landSize);
  const handleTotalStory = useFormHandler("totalStory", validator.totalStory);
  const handleUndergroundStory = useFormHandler("undergroundStory", validator.undergroundStory);
  const handleStructure = useFormHandler("structure", validator.structure);
  const handleHouseSize = useFormHandler("houseSize", validator.houseSize);

  // 接道状況
  const [connecting, setConnecting] = React.useState<Connecting>({
    status: "",
    details: [],
  });
  const [validateConnectingStatus, setValidateConnectingStatus] = React.useState("");
  const [validateConnectingDetails, setValidateConnectingDetails] = React.useState<ConnectingDetail[]>([]);

  const handleChangeConnectingStatus = (ev: React.ChangeEvent<HTMLSelectElement>) => {
    const value = ev.target.value as IConnectingStatus;
    const validateMessage = connectingValidator.status(value);
    const newLength = switchConnectingRoadStatusNumber(value);
    const newDetails = range(0, newLength).map(
      (): ConnectingDetail => ({
        type: "" as IConnectingType,
        direction: "" as IConnectingDirection,
        width: "",
        contactWidth: "",
      })
    );
    const newState = [...connecting.details, ...newDetails].slice(0, newLength);
    setConnecting(() => ({
      status: value,
      details: newState,
    }));
    const newValidateState = [...validateConnectingDetails, ...newDetails].slice(0, newLength);
    setValidateConnectingStatus(validateMessage);
    setValidateConnectingDetails(newValidateState);
  };

  const handleChangeConnectingDetails = (
    index: number,
    key: "type" | "direction" | "width" | "contactWidth",
    detail: ConnectingDetail
  ) => (ev: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { value } = ev.target;
    const details = [...connecting.details];
    const newState = {
      ...detail,
      [key]: value,
    };
    details[index] = newState;
    setConnecting(() => ({
      ...connecting,
      details,
    }));
    const newValidateDetails = [...validateConnectingDetails];
    const newValidateDetail = {
      ...newValidateDetails[index],
      [key]: connectingValidator[key](value),
    };
    newValidateDetails[index] = newValidateDetail;
    setValidateConnectingDetails(newValidateDetails);
  };

  const handleNext = () => {
    firstRef.current = false;
    onNext();
  };
  const isTrafficInfoAutoLinked = featuresObj[Feature.TRAFFIC_INFO_AUTO_LINK]?.enabled;
  const isConnectingRoadInfoAutoEnabled = featuresObj[Feature.CONNECTING_ROAD_INFO_AUTO]?.enabled;

  const isValid =
    isValidProperty(isTrafficInfoAutoLinked) &&
    isValidPropertyDetails(isConnectingRoadInfoAutoEnabled) &&
    isRequiredPropertyDetails(isConnectingRoadInfoAutoEnabled);

  React.useEffect(() => {
    let updateValues = {
      values: {
        story: "",
        unitSize: "",
        balconyDirection: "",
        floorPlan: "",
        roomNumber: "",
        buildingName: "",
      },
      validates: {
        story: "",
        unitSize: "",
        balconyDirection: "",
        floorPlan: "",
        roomNumber: "",
      },
    };
    const unitObj = units.find(u => `${u.id}` === unit);
    if (unitObj) {
      updateValues = {
        ...updateValues,
        values: {
          story: `${ifResponseValueBlank(unitObj.story)}`,
          unitSize: `${ifResponseValueBlank(unitObj.unitSize)}`,
          balconyDirection: ifResponseValueBlank(unitObj.balconyDirection),
          floorPlan: ifResponseValueBlank(unitObj.floorPlan),
          roomNumber: ifResponseValueBlank(unitObj.roomNumber),
          buildingName: ifResponseValueBlank(unitObj.buildingName),
        },
      };
    }
    dispatch(updateFormValues(updateValues));
  }, [unit]);

  React.useEffect(() => {
    dispatch(
      updateFormValues({
        values: {
          connecting,
        },
        validates: {
          connecting: validateConnectingDetails.map(v => Object.values(v).join("")).join(""),
        },
      })
    );
  }, [connecting]);

  const hiddenLivingAddressComponent = isHiddenComponent(featuresObj, HIDDEN_COMPONENT_OPTIONS.CUSTOMER_ADDRESS);

  const hiddenPurchaseIntentionComponent = isHiddenComponent(featuresObj, HIDDEN_COMPONENT_OPTIONS.PURCHASE_INTENTION);

  /**
   * 売却物件[所在地]部屋番号変更時、
   * [お住いの住所]建物名を更新する
   */
  React.useEffect(() => {
    if (values.isSame && !hiddenLivingAddressComponent) {
      dispatch(
        updateFormValues({
          values: {
            building: `${isBlank(validates.propertyName) ? values.propertyName : ""}${
              values.buildingName ? values.buildingName : ""}${
              isBlank(validates.roomNumber) ? values.roomNumber : ""
            }`,
          },
          validates: {
            building: "",
          },
        })
      );
    }
  }, [values.roomNumber, hiddenLivingAddressComponent]);

  const isMansion = PropertyType.MANSION === values.propertyType;
  const isHouse = PropertyType.HOUSE === values.propertyType;

  const COLOR_THEME: { has: boolean; extras?: ColorThemeExtras } = {
    has: Boolean(featuresObj[Feature.COLOR_THEME]),
    extras: featuresObj[Feature.COLOR_THEME]?.color,
  };

  const unitOptions: Option[] = React.useMemo(() => {
    return units.map(mansionUnitResponse => {
      const { id, roomNumber, unitSize, story, floorPlan, balconyDirection, buildingName } = mansionUnitResponse;
      let label = `${defaultIfBlank(roomNumber, '-')}号室 (${
        defaultIfBlank(unitSize, '-')}㎡/${defaultIfBlank(story, "-")
      }階/${defaultIfBlank(floorPlan, '-')}/${defaultIfBlank(balconyDirection, '-')})`;
      if (buildingName) {
        label += ` (${buildingName})`;
      }

      return { label, value: `${id}` };
    });
  }, [units]);

  return (
    <Article>
      <Article.Header>
        <MiniTitle>売却物件について教えてください</MiniTitle>
      </Article.Header>
      <Article.Block>
        {isMansion && (
          <FormContainer>
            {units.length > 0 && (
              <FormBlock className="underline">
                <FormBlock.Label
                  value="住戸を選択"
                  background={COLOR_THEME.extras?.required_icon?.background}
                  text={COLOR_THEME.extras?.required_icon?.text}
                  border={COLOR_THEME.extras?.required_icon?.border}
                />
                <FormBlock.Row>
                  <FormBlock.Select
                    description="既存の住戸から選択"
                    options={unitOptions}
                    onChange={ev => setUnit(ev.target.value)}
                    value={unit}
                    aria-label="既存の住戸から選択"
                    background={COLOR_THEME.extras?.invalid_state?.background}
                  />
                </FormBlock.Row>
              </FormBlock>
            )}
            <FormBlock>
              <FormBlock.Label
                value="住戸情報"
                required
                background={COLOR_THEME.extras?.required_icon?.background}
                text={COLOR_THEME.extras?.required_icon?.text}
                border={COLOR_THEME.extras?.required_icon?.border}
              />
              <FormBlock.Row>
                <FormBlock.Input
                  description="部屋番号"
                  aria-label="部屋番号"
                  after="号室"
                  placeholder="例）103"
                  onChange={ev => handleRoomNumber(ev.target.value)}
                  width="42%"
                  value={values.roomNumber}
                  required
                  validateMessage={validates.roomNumber}
                  background={COLOR_THEME.extras?.invalid_state?.background}
                />
              </FormBlock.Row>
              <FormBlock.Row>
                <FormBlock.Input
                  description="所在階"
                  aria-label="所在階"
                  after="階"
                  placeholder="例）10"
                  onChange={ev => handleStory(ev.target.value)}
                  width="40%"
                  value={values.story}
                  required
                  validateMessage={validates.story}
                  background={COLOR_THEME.extras?.invalid_state?.background}
                />
                <FormBlock.Input
                  description="専有面積"
                  aria-label="専有面積"
                  placeholder="例）50.0"
                  after="㎡"
                  onChange={ev => handleUnitSize(ev.target.value)}
                  width="40%"
                  value={values.unitSize}
                  required
                  validateMessage={validates.unitSize}
                  background={COLOR_THEME.extras?.invalid_state?.background}
                />
              </FormBlock.Row>
              <FormBlock.Row>
                <FormBlock.Select
                  description="間取り"
                  aria-label="間取り"
                  options={floorPlanOptions}
                  onChange={ev => handleFloorPlan(ev.target.value)}
                  width="40%"
                  value={values.floorPlan}
                  required
                  validateMessage={validates.floorPlan}
                  background={COLOR_THEME.extras?.invalid_state?.background}
                />
                <FormBlock.Select
                  description="バルコニー方向"
                  aria-label="バルコニー方向"
                  options={balconyDirectionOptions}
                  onChange={ev => handleBalconyDirection(ev.target.value)}
                  width="40%"
                  value={values.balconyDirection}
                  required
                  validateMessage={validates.balconyDirection}
                  background={COLOR_THEME.extras?.invalid_state?.background}
                />
              </FormBlock.Row>
            </FormBlock>
          </FormContainer>
        )}
        {!isMansion && (
          <FormContainer>
            <FormBlock>
              <FormBlock.Label
                value="土地面積"
                required
                background={COLOR_THEME.extras?.required_icon?.background}
                text={COLOR_THEME.extras?.required_icon?.text}
                border={COLOR_THEME.extras?.required_icon?.border}
              />
              <FormBlock.Row>
                <FormBlock.Input
                  after="㎡"
                  placeholder="例）100.0"
                  onChange={ev => handleLandSize(ev.target.value)}
                  aria-label="土地面積"
                  width="40%"
                  value={values.landSize}
                  required
                  validateMessage={validates.landSize}
                  background={COLOR_THEME.extras?.invalid_state?.background}
                />
              </FormBlock.Row>
            </FormBlock>
          </FormContainer>
        )}
        {isHouse && (
          <FormContainer>
            <FormBlock>
              <FormBlock.Label
                value="建物情報"
                required
                background={COLOR_THEME.extras?.required_icon?.background}
                text={COLOR_THEME.extras?.required_icon?.text}
                border={COLOR_THEME.extras?.required_icon?.border}
              />
              <FormBlock.Row>
                <FormBlock.Input
                  before="地上"
                  after="階"
                  aria-label="地上の階層"
                  placeholder="例）10"
                  onChange={ev => handleTotalStory(ev.target.value)}
                  width="20%"
                  value={values.totalStory}
                  required
                  validateMessage={validates.totalStory}
                  background={COLOR_THEME.extras?.invalid_state?.background}
                />
                <FormBlock.Input
                  before="地下"
                  after="階"
                  aria-label="地下の階層"
                  placeholder="例）10"
                  onChange={ev => handleUndergroundStory(ev.target.value)}
                  width="20%"
                  value={values.undergroundStory}
                  validateMessage={validates.undergroundStory}
                  background={COLOR_THEME.extras?.invalid_state?.background}
                />
              </FormBlock.Row>
              <FormBlock.Row>
                <FormBlock.Select
                  description="建物構造"
                  aria-label="建物構造"
                  options={structureOptions}
                  onChange={ev => handleStructure(ev.target.value)}
                  width="40%"
                  value={values.structure}
                  required
                  validateMessage={validates.structure}
                  background={COLOR_THEME.extras?.invalid_state?.background}
                />
              </FormBlock.Row>
              <FormBlock.Row>
                <FormBlock.Input
                  description="建物延床面積"
                  aria-label="建物延床面積"
                  after="㎡"
                  placeholder="例）100.0"
                  onChange={ev => handleHouseSize(ev.target.value)}
                  width="25%"
                  value={values.houseSize}
                  required
                  validateMessage={validates.houseSize}
                  background={COLOR_THEME.extras?.invalid_state?.background}
                />
                <FormBlock.Select
                  description="間取り"
                  aria-label="間取り"
                  options={floorPlanOptions}
                  onChange={ev => handleFloorPlan(ev.target.value)}
                  width="25%"
                  value={values.floorPlan}
                  required
                  validateMessage={validates.floorPlan}
                  background={COLOR_THEME.extras?.invalid_state?.background}
                />
              </FormBlock.Row>
            </FormBlock>
          </FormContainer>
        )}
        {!isMansion && (
          <FormContainer>
            <FormBlock className="underline">
              <FormBlock.Label
                value="道路情報"
                required
                background={COLOR_THEME.extras?.required_icon?.background}
                text={COLOR_THEME.extras?.required_icon?.text}
                border={COLOR_THEME.extras?.required_icon?.border}
              />
              <FormBlock.Row>
                <FormBlock.Select
                  description="接道状況"
                  aria-label="接道状況"
                  options={connectingStatusOptions}
                  width="40%"
                  onChange={handleChangeConnectingStatus}
                  required
                  validateMessage={validateConnectingStatus}
                  value={connecting.status}
                  background={COLOR_THEME.extras?.invalid_state?.background}
                />
              </FormBlock.Row>
            </FormBlock>
            {connecting.details.map((d, i) => {
              return (
                <FormBlock className="underline" key={`${i + 1}`}>
                  <FormBlock.Label
                    value="接道情報"
                    required
                    background={COLOR_THEME.extras?.required_icon?.background}
                    text={COLOR_THEME.extras?.required_icon?.text}
                    border={COLOR_THEME.extras?.required_icon?.border}
                  />
                  <FormBlock.Row>
                    <FormBlock.Select
                      description="接道種別"
                      aria-label="接道種別"
                      options={connectingTypeOptions}
                      width="40%"
                      onChange={handleChangeConnectingDetails(i, "type", d)}
                      value={d.type}
                      required
                      validateMessage={validateConnectingDetails[i].type}
                      background={COLOR_THEME.extras?.invalid_state?.background}
                    />
                    <FormBlock.Select
                      description="接道方向"
                      aria-label="接道方向"
                      options={connectingDirectionOptions}
                      onChange={handleChangeConnectingDetails(i, "direction", d)}
                      value={d.direction}
                      required
                      validateMessage={validateConnectingDetails[i].direction}
                      background={COLOR_THEME.extras?.invalid_state?.background}
                    />
                  </FormBlock.Row>
                  <FormBlock.Row>
                    <FormBlock.Input
                      description="接道幅員"
                      aria-label="接道幅員"
                      after="ｍ"
                      placeholder="例）1"
                      width="20%"
                      onChange={handleChangeConnectingDetails(i, "width", d)}
                      value={d.width}
                      required
                      validateMessage={validateConnectingDetails[i].width}
                      background={COLOR_THEME.extras?.invalid_state?.background}
                    />
                    <FormBlock.Input
                      description="接道接面"
                      aria-label="接道接面"
                      placeholder="例）1"
                      after="ｍ"
                      width="20%"
                      onChange={handleChangeConnectingDetails(i, "contactWidth", d)}
                      value={d.contactWidth}
                      required
                      validateMessage={validateConnectingDetails[i].contactWidth}
                      background={COLOR_THEME.extras?.invalid_state?.background}
                    />
                  </FormBlock.Row>
                </FormBlock>
              );
            })}
          </FormContainer>
        )}
      </Article.Block>
      <NextButton
        disabled={firstRef.current && !isValid}
        value={hiddenPurchaseIntentionComponent ? "もう少しで完了します" : "次へ進む"}
        onClick={handleNext}
      />
    </Article>
  );
};

export default PropertyDetailBlockOld;
